import { useEffect, useRef, useState, useCallback } from 'react';
import ShaderBackground from './ShaderBackground';
import GridOverlay from './GridOverlay';

const SCRAMBLE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
const TITLE = 'Apiron.';
const FINAL = TITLE.split('');

const KONAMI = ['ArrowUp','ArrowUp','ArrowDown','ArrowDown','ArrowLeft','ArrowRight','ArrowLeft','ArrowRight','b','a'];

const SUBSTRATE_LINES = [
  { text: 'Apiron  [a\u0304-p\u012B-r\u014Fn]', cls: 'egg-heading' },
  { text: 'Noun / Technical Abstract', cls: 'egg-sub' },
  { text: '', cls: 'egg-spacer' },
  { text: 'The fundamental, boundless substrate', cls: 'egg-body' },
  { text: 'for autonomous logic.', cls: 'egg-body' },
  { text: '', cls: 'egg-spacer' },
  { text: 'A declarative architectural layer designed', cls: 'egg-body' },
  { text: 'to abstract the entropy of global data into', cls: 'egg-body' },
  { text: 'structured institutional intelligence.', cls: 'egg-body' },
  { text: '', cls: 'egg-spacer' },
  { text: 'The origin point where legacy operations', cls: 'egg-body' },
  { text: 'meet autonomous execution.', cls: 'egg-body' },
];

const ARCHITECT_LINES = [
  'APIRON',
  '',
  'A technical framework for the preservation',
  'and scaling of intellectual capital.',
  '',
  'We build the digital refineries that transform',
  'fragmented technological assets into',
  'high-fidelity, autonomous infrastructure.',
];

export default function ApironLanding() {
  const mouseRef = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const charRefs = useRef([]);
  const titleRef = useRef(null);
  const animDoneRef = useRef(false);
  const konamiRef = useRef([]);
  const [chars, setChars] = useState(FINAL.map(() => '\u00A0'));
  const [eggVisible, setEggVisible] = useState(false);
  const [konamiFlash, setKonamiFlash] = useState(false);

  // Console Easter Egg (Option 2)
  useEffect(() => {
    const style = 'color:#00F0FF;font-family:monospace;font-size:11px;line-height:1.7;background:#0A0A0A;padding:12px 16px;border-left:2px solid #00F0FF';
    const manifest = `%c
 [MANIFEST]: APIRON_CORE_V1.0
 [CLASSIFICATION]: Private / Proprietary
 [LOGIC]: "The bridge between raw data and autonomous intent."
 [INFRASTRUCTURE]: Governed AI & Intellectual Property
 [NOTE]: Stability is found in the abstraction.
`;
    console.log(manifest, style);
  }, []);

  // Konami code listener (Option 3)
  useEffect(() => {
    const onKey = (e) => {
      konamiRef.current.push(e.key);
      konamiRef.current = konamiRef.current.slice(-KONAMI.length);
      if (konamiRef.current.length === KONAMI.length && konamiRef.current.every((k, i) => k === KONAMI[i])) {
        konamiRef.current = [];
        setKonamiFlash(true);
        setTimeout(() => setKonamiFlash(false), 12000);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Global mouse tracking
  useEffect(() => {
    const onMove = (e) => { mouseRef.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  // Title scramble-in
  useEffect(() => {
    let raf;
    let t0 = null;
    animDoneRef.current = false;

    const tick = (ts) => {
      if (!t0) t0 = ts;
      const elapsed = ts - t0;
      let allDone = true;

      setChars(
        FINAL.map((ch, i) => {
          const start = 600 + i * 80;
          if (elapsed < start) { allDone = false; return '\u00A0'; }
          const progress = Math.min((elapsed - start) / 300, 1);
          if (progress < 1) {
            allDone = false;
            return Math.random() < progress
              ? ch
              : SCRAMBLE[Math.floor(Math.random() * SCRAMBLE.length)];
          }
          return ch;
        })
      );

      if (!allDone) {
        raf = requestAnimationFrame(tick);
      } else {
        animDoneRef.current = true;
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Magnetic + Parallax + Title jitter loop
  useEffect(() => {
    const magneticEls = document.querySelectorAll('[data-magnetic]');
    const parallaxEls = document.querySelectorAll('[data-parallax]');
    let raf;

    const update = () => {
      const { x, y } = mouseRef.current;
      const hw = window.innerWidth / 2;
      const hh = window.innerHeight / 2;

      parallaxEls.forEach((el) => {
        const f = parseFloat(el.dataset.parallax);
        el.style.transform = `translate(${(x - hw) * f}px, ${(y - hh) * f}px)`;
      });

      magneticEls.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = x - cx;
        const dy = y - cy;
        const dist = Math.hypot(dx, dy);
        if (dist < 150) {
          const force = (1 - dist / 150) * 0.35;
          el.style.transform = `translate(${dx * force}px, ${dy * force}px)`;
        } else {
          el.style.transform = '';
        }
      });

      if (titleRef.current) {
        const tr = titleRef.current.getBoundingClientRect();
        const tcx = tr.left + tr.width / 2;
        const tcy = tr.top + tr.height / 2;
        const td = Math.hypot(x - tcx, y - tcy);
        charRefs.current.forEach((el, i) => {
          if (!el) return;
          if (td < 400) {
            const force = (1 - td / 400) * 0.3;
            el.style.transform = `translateY(${Math.sin(Date.now() * 0.003 + i * 1.2) * force * 4}px)`;
          } else {
            el.style.transform = '';
          }
        });
      }

      raf = requestAnimationFrame(update);
    };

    raf = requestAnimationFrame(update);
    return () => cancelAnimationFrame(raf);
  }, []);

  const onTitleHover = useCallback(() => {
    if (!animDoneRef.current) return;
    FINAL.forEach((ch, i) => {
      setTimeout(() => {
        setChars(prev => {
          const n = [...prev];
          n[i] = SCRAMBLE[Math.floor(Math.random() * SCRAMBLE.length)];
          return n;
        });
        setTimeout(() => {
          setChars(prev => { const n = [...prev]; n[i] = ch; return n; });
        }, 80 + Math.random() * 120);
      }, Math.random() * 200);
    });
  }, []);

  // Dot click → Substrate Easter Egg (Option 1)
  const onDotClick = useCallback((e) => {
    e.stopPropagation();
    setEggVisible(true);
  }, []);

  const onContact = useCallback((e) => {
    e.preventDefault();
    if (!e.isTrusted) return;
    const addr = ['contact', '@', 'apiron', '.com'].join('');
    const subj = encodeURIComponent('Inquiry ' + String.fromCharCode(8212) + ' Apiron');
    window.location.href = 'mailto:' + addr + '?subject=' + subj;
  }, []);

  return (
    <div className="apiron-landing" data-testid="apiron-landing">
      <ShaderBackground />
      <GridOverlay />
      <div className="noise-overlay" />
      <div className="glow-cyan" data-parallax="0.04" />
      <div className="glow-orange" data-parallax="0.035" />

      <main>
        <header data-parallax="0.015">
          <div className="logo-mark" data-magnetic data-testid="logo-mark">
            Apiron
          </div>
          <div className="status-live" data-testid="status-live">
            <span className="status-dot" />
            System Active
          </div>
          <div className="reg-tag" data-testid="reg-tag">Est. 1995</div>
        </header>

        <div className="center" data-parallax="0.01">
          <h1
            className="title"
            ref={titleRef}
            data-text="Apiron."
            onMouseEnter={onTitleHover}
            data-testid="main-title"
          >
            {chars.map((c, i) => (
              <span
                key={i}
                className={`char${i === TITLE.length - 1 ? ' dot' : ''}`}
                ref={(el) => { charRefs.current[i] = el; }}
                onClick={i === TITLE.length - 1 ? onDotClick : undefined}
                style={i === TITLE.length - 1 ? { cursor: 'pointer' } : undefined}
              >
                {c}
              </span>
            ))}
          </h1>

          <p className="tagline" data-testid="tagline">
            A private holding company investing in intellectual property,
            autonomous systems, and next-generation technology infrastructure.
          </p>

          <div className="divider" />

          <div className="focus-areas" data-testid="focus-areas">
            {['IP & Technology', 'Autonomous Systems', 'Governed AI'].map((area, i) => (
              <div
                key={area}
                className="focus-item"
                data-magnetic
                data-testid={`focus-item-${i}`}
                style={{ animationDelay: `${2.6 + i * 0.15}s` }}
              >
                {area}
              </div>
            ))}
          </div>
        </div>

        <footer data-parallax="0.02">
          <div className="footer-copy" data-testid="footer-copy">
            &copy; 2026 Apiron. All rights reserved.
          </div>
          <button
            className="contact-btn"
            onClick={onContact}
            data-magnetic
            aria-label="Contact"
            data-testid="contact-btn"
          >
            Contact
          </button>
        </footer>
      </main>

      {/* Easter Egg Overlay — Substrate Definition (click cyan dot) */}
      {eggVisible && (
        <div
          className="egg-overlay"
          data-testid="egg-overlay"
          onClick={() => setEggVisible(false)}
        >
          <div className="egg-panel" onClick={(e) => e.stopPropagation()}>
            <div className="egg-terminal-bar">
              <span className="egg-terminal-dot" style={{ background: '#FF5F56' }} />
              <span className="egg-terminal-dot" style={{ background: '#FFBD2E' }} />
              <span className="egg-terminal-dot" style={{ background: '#27C93F' }} />
              <span className="egg-terminal-label">substrate.def</span>
              <button className="egg-close" onClick={() => setEggVisible(false)}>&times;</button>
            </div>
            <div className="egg-content">
              {SUBSTRATE_LINES.map((line, i) => (
                <div
                  key={i}
                  className={`egg-line ${line.cls}`}
                  style={{ animationDelay: `${i * 0.08}s` }}
                >
                  {line.text || '\u00A0'}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Konami Code Flash — Architect Definition */}
      {konamiFlash && (
        <div className="konami-overlay" data-testid="konami-overlay">
          <div className="konami-content">
            {ARCHITECT_LINES.map((line, i) => (
              <div
                key={i}
                className="konami-line"
                style={{ animationDelay: `${i * 0.12}s` }}
              >
                {line || '\u00A0'}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
